import VueAxios from 'vue-axios';
import axios from 'axios';
import normalize from 'json-api-normalizer';
import {
  values,
  kebabCase,
  camelCase,
  each,
  isArray,
  isPlainObject,
} from 'lodash';

import app from '@/main';
import store from '@/store';
import { apiOrigin } from '@/constants';
import { pluralize } from '@/utils/case';

// Transforms a record from `json-api-normalizer` format to `vuex-orm` format.
// TODO: Make this more efficient, use `transform`?
function transformRecord(record) {
  const payload = { id: record.id, ...record.attributes };

  each(record.relationships, (rel, key) => {
    // Skip over degenerate cases where the backend gives no data.
    if (!rel.data) return;

    if (rel.data.id) {
      payload[`${key}Id`] = rel.data.id;

      if (rel.data.type === key) {
        // belongsTo
        payload[key] = { id: rel.data.id };
      } else {
        // polymorphic belongsTo
        payload[`${key}Type`] = kebabCase(pluralize(rel.data.type));
      }
    } else {
      // hasMany
      payload[key] = rel.data.map((r) => ({ id: r.id }));
    }
  });

  return payload;
}

function parseLinks(linkHeader) {
  const links = {};

  linkHeader.split(', ').forEach((header) => {
    const matchGroup = header.match(/<(.+)>; rel="(.+)"/);
    if (matchGroup && matchGroup[1] && matchGroup[2]) {
      links[matchGroup[2]] = matchGroup[1];
    }
  });

  return links;
}

function parsePaginationHeaders(headers, type) {
  const perPage = parseInt(headers['per-page'], 10);
  const total = parseInt(headers.total, 10);

  store.dispatch('setResourceLinks', {
    type: kebabCase(pluralize(type)),
    data: {
      links: parseLinks(headers.link),
      perPage,
      total,
      pageCount: Math.ceil(total / perPage),
    },
  });
}

export default function (Vue) {
  const instance = axios.create({
    baseURL: apiOrigin,
    headers: {
      'Content-Type': 'application/vnd.api+json',
      'Cache-Control': 'no-cache',
    },

    adapter: axios.defaults.adapter,
  });

  instance.interceptors.request.use(function (config) {
    // Our entities often have dashes in names but the Rails resources use
    // underscores for names.
    config.url = config.url.replace(/-/g, '_');
    return config;
  });

  instance.interceptors.request.use(
    function (config) {
      if (['/moves', '/hand-results'].includes(config.url)) {
        return config;
      }

      // TODO: Avoid showing the progress bar if using a cached response.
      app.$Progress.start();
      return config;
    },
    function (error) {
      app.$Progress.fail();
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    function (response) {
      app.$Progress.finish();
      return response;
    },
    function (error) {
      app.$Progress.fail();
      return Promise.reject(error);
    }
  );

  // TODO: Consider moving this all to `dataTransformer`.
  // See https://vuex-orm.github.io/plugin-axios/guide/configurations.html#datatransformer
  // Currently it seems to double insert due to the `insertOrUpdate` call below.
  // The library still does its own insert too.
  instance.interceptors.response.use(function (response) {
    // TODO: Sometimes the response already comes correctly formatted. Look
    // into why this happens later. Is it cached?
    if (!response.data) {
      return response;
    }

    const data = response.data.data;

    // Endpoints like `devise_token_auth` ones do not return JSON:API data.
    if (!data || (isPlainObject(data) && !data.type)) {
      return response;
    }

    // Can ignore empty index responses.
    if (isArray(data) && data.length === 0) {
      return response;
    }

    const normalized = normalize(response.data);

    Object.keys(normalized).forEach((normType) => {
      const action = `entities/${kebabCase(
        pluralize(normType)
      )}/insertOrUpdate`;

      store.dispatch(action, {
        data: values(normalized[normType]).map((record) =>
          transformRecord(record)
        ),
      });
    });

    const type = camelCase(isArray(data) ? data[0].type : data.type);

    if (response.headers.link) {
      parsePaginationHeaders(response.headers, type);
    }

    if (isArray(data)) {
      const ids = data.map((r) => r.id);

      response.data.data = values(normalized[type])
        .map((r) => transformRecord(r))
        .filter((r) => ids.includes(r.id));
    } else {
      response.data.data = transformRecord(normalized[type][data.id]);
    }

    return response;
  });

  Vue.use(VueAxios, instance);
}
